import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { Error } from "../../../../../../../models/error";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../../../models/statuses";
import StatusesService from "../../../../../../../services/statuses.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [statuses, setStatuses] = useState<Statuses[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["job.sub.status.list", page, pageSize, filters, searches, sorting],
    () =>
      StatusesService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["id", "desc"]])
          : JSON.stringify([["id", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setStatuses(response.data);
      },
    }
  );

  const { refetch: getJobSubStatusTotal } = useQuery(
    ["job.sub.status.count", filters, searches],
    () =>
      StatusesService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createStatus, isLoading: isCreating } = useMutation(
    ["job.sub.status.create"],
    (status: Statuses) => StatusesService.create(status),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getJobSubStatusTotal();
      },
      onError: (err: AxiosError<Error>) => {
        NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateStatus, isLoading: isUpdating } = useMutation(
    ["job.sub.status.update"],
    ({ id, status }: { id: number; status: Statuses }) => StatusesService.update(id, status),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getJobSubStatusTotal();
      },
      onError: (err: AxiosError<Error>) => {
        NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    statuses,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading: isFetching || isCreating || isUpdating,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createStatus,
    updateStatus,
  };
};

export default useActions;
